import {
  ActionFunctionMap,
  AnyEventObject,
  assign,
  ConditionPredicate,
  EventObject,
  Machine,
  TransitionConfigOrTarget,
  TransitionsConfig,
} from 'xstate';
import steps from './steps';

export type Context = {
  status: EvaluationStatus;
  isAtEnrollment: boolean;
  admissionType: AdmissionType;
  history: string[];
};

export type AdmissionType = 'essay' | 'enem' | 'secondGraduation' | 'transferGraduation' | null;

export type EvaluationStatus = 'failure' | 'pendingApproval' | 'retryEssay' | 'success' | null;

export interface EvaluationResultEvent extends AnyEventObject {
  status?: EvaluationStatus;
}

export interface TargetSelectionEvent extends AnyEventObject {
  value?: string;
}

export interface AdmissionTypeEvent extends AnyEventObject {
  value?: AdmissionType;
}

export interface IsAtEnrollmentEvent extends AnyEventObject {
  isAtEnrollment?: boolean;
}

export const actions: ActionFunctionMap<Context, AnyEventObject> = {
  setStatus: assign({
    status: (_, event: EvaluationResultEvent) => event.status || null,
  }),
  setAdmissionType: assign({
    admissionType: (_, event: AdmissionTypeEvent) => event.value || null,
  }),
  updateHistory: assign({
    history: (ctx, event, { state }) => {
      if (event.type !== 'NEXT') return ctx.history;

      const [currentStateMeta]: any[] = Object.values(state?.meta);
      const historySet = new Set(ctx.history);
      historySet.add(currentStateMeta.step);
      return Array.from(historySet);
    },
  }),
  setRetryHistory: assign({
    history: ctx => {
      return [
        steps.courseSelection,
        steps.citySelection,
        steps.campusSelection,
        steps.admissionType.selection,
      ];
    },
  }),
  setIsAtEnrollment: assign({
    isAtEnrollment: (_, event: IsAtEnrollmentEvent) => event.isAtEnrollment || false,
  }),
  resetHistory: assign({
    history: (ctx, _, { state }) => {
      // TO DO: Map other history field maps to resetHistory to
      const HISTORY_FIELD_MAP = ['courseSelection', 'citySelection', 'campusSelection'];
      const [currentStateMeta]: any[] = Object.values(state?.meta);
      const historySet = new Set(
        HISTORY_FIELD_MAP.slice(0, HISTORY_FIELD_MAP.indexOf(currentStateMeta.step)),
      );
      return Array.from(historySet);
    },
  }),
};

export const guards: Record<string, ConditionPredicate<Context, AnyEventObject>> = {
  evaluateStatus: (ctx, _, { cond }: any) => ctx.status === cond.status,
  hasEventProp: (_, event, { cond }: any) => {
    const hasProp = Object.keys(event).includes(cond.property);
    const hasValues = cond.values ? cond.values.includes(event[cond.property]) : true;
    return hasProp && hasValues;
  },
  canNavigateToStep: (ctx, _, { cond }: any) => {
    const isVisited = ctx.history.includes(cond.step);

    return isVisited;
  },
  selectTarget: (_, event: TargetSelectionEvent, { cond }: any) => event.value === cond.value,
};

export const evaluationStatusValues: EvaluationStatus[] = [
  'failure',
  'pendingApproval',
  'retryEssay',
  'success',
];

export const returnableSteps = [
  steps.courseSelection,
  steps.citySelection,
  steps.campusSelection,
  steps.admissionType.selection,
  steps.admissionType.enem.yearSelection,
  steps.admissionType.enem.subscriptionNumber,
  steps.admissionType.enem.grades,
  steps.admissionType.secondGraduation.institutionInfo,
  steps.admissionType.secondGraduation.documentation,
  steps.admissionType.transferGraduation.institutionInfo,
  steps.admissionType.transferGraduation.documentation,
  steps.admissionType.essay.themeSelection,
  steps.admissionType.essay.supportText,
  steps.admissionType.essay.write,
];

// Ordem dos passos do fluxo, usada apenas para reparar o `history` de um estado
// persistido antigo (ver `repairHistory` em `src/pipeline/index.ts`). Duplica
// implicitamente a topologia da máquina: passo novo entra aqui também, e o teste que
// compara com `returnableSteps` reprova se esquecerem.
export const linearFlow: string[] = [
  steps.courseSelection,
  steps.citySelection,
  steps.campusSelection,
  steps.admissionType.selection,
];

export const branchFlow: Record<Exclude<AdmissionType, null>, string[]> = {
  enem: [
    steps.admissionType.enem.yearSelection,
    steps.admissionType.enem.subscriptionNumber,
    steps.admissionType.enem.grades,
  ],
  secondGraduation: [
    steps.admissionType.secondGraduation.institutionInfo,
    steps.admissionType.secondGraduation.documentation,
  ],
  transferGraduation: [
    steps.admissionType.transferGraduation.institutionInfo,
    steps.admissionType.transferGraduation.documentation,
  ],
  essay: [
    steps.admissionType.essay.themeSelection,
    steps.admissionType.essay.supportText,
    steps.admissionType.essay.write,
  ],
};

export const globalEvents: TransitionsConfig<Context, EventObject> = {
  ...returnableSteps.reduce((acc, step) => {
    const event: string = `GO_TO_${step.toUpperCase()}`;

    acc[event] = { target: step, cond: { type: 'canNavigateToStep', step } };

    return acc;
  }, {} as { [key: string]: TransitionConfigOrTarget<Context, EventObject> }),
  UPDATE_PIPELINE_STATUS: {
    target: 'evaluationResult',
    cond: {
      type: 'hasEventProp',
      property: 'status',
      values: evaluationStatusValues,
    },
    actions: ['setStatus'],
  },
  UPDATE_PIPELINE_ISATENROLLMENT: {
    target: 'evaluationResult',
    cond: {
      type: 'hasEventProp',
      property: 'isAtEnrollment',
      values: [true, false],
    },
    actions: ['setIsAtEnrollment'],
  },
};

export default Machine<Context>(
  {
    id: 'admissions',
    initial: 'courseSelection',
    context: {
      status: null,
      isAtEnrollment: false,
      admissionType: null,
      history: [],
    },
    states: {
      courseSelection: {
        meta: { step: steps.courseSelection },
        exit: ['updateHistory'],
        on: {
          NEXT: 'citySelection',
          RESET_HISTORY: {
            actions: ['resetHistory'],
          },
        },
      },
      citySelection: {
        meta: { step: steps.citySelection },
        exit: ['updateHistory'],
        on: {
          PREV: 'courseSelection',
          NEXT: 'campusSelection',
          RESET_HISTORY: {
            actions: ['resetHistory'],
          },
        },
      },
      campusSelection: {
        meta: { step: steps.campusSelection },
        exit: ['updateHistory'],
        on: {
          PREV: 'citySelection',
          NEXT: 'admissionType',
          RESET_HISTORY: {
            actions: ['resetHistory'],
          },
        },
      },
      admissionType: {
        initial: 'selection',
        states: {
          selection: {
            meta: { step: steps.admissionType.selection },
            exit: ['updateHistory'],
            on: {
              PREV: '#admissions.campusSelection',
              NEXT: [
                {
                  target: 'essay',
                  cond: { type: 'selectTarget', value: 'essay' },
                  actions: ['setAdmissionType'],
                },
                {
                  target: 'enem',
                  cond: { type: 'selectTarget', value: 'enem' },
                  actions: ['setAdmissionType'],
                },
                {
                  target: 'secondGraduation',
                  cond: { type: 'selectTarget', value: 'secondGraduation' },
                  actions: ['setAdmissionType'],
                },
                {
                  target: 'transferGraduation',
                  cond: { type: 'selectTarget', value: 'transferGraduation' },
                  actions: ['setAdmissionType'],
                },
              ],
            },
          },
          essay: {
            initial: 'themeSelection',
            states: {
              themeSelection: {
                meta: { step: steps.admissionType.essay.themeSelection },
                exit: ['updateHistory'],
                on: {
                  PREV: { target: '#admissions.admissionType' },
                  NEXT: 'supportText',
                },
              },
              supportText: {
                meta: { step: steps.admissionType.essay.supportText },
                exit: ['updateHistory'],
                on: {
                  PREV: 'themeSelection',
                  NEXT: 'write',
                },
              },
              write: {
                meta: { step: steps.admissionType.essay.write },
                exit: ['updateHistory'],
                on: {
                  PREV: 'supportText',
                  NEXT: {
                    target: '#admissions.evaluationResult',
                    cond: {
                      type: 'hasEventProp',
                      property: 'status',
                      values: evaluationStatusValues,
                    },
                    actions: ['setStatus'],
                  },
                },
              },
            },
          },
          enem: {
            initial: 'yearSelection',
            states: {
              yearSelection: {
                meta: { step: steps.admissionType.enem.yearSelection },
                exit: ['updateHistory'],
                on: {
                  PREV: '#admissions.admissionType',
                  NEXT: 'subscriptionNumber',
                },
              },
              subscriptionNumber: {
                meta: { step: steps.admissionType.enem.subscriptionNumber },
                exit: ['updateHistory'],
                on: {
                  PREV: 'yearSelection',
                  NEXT: 'grades',
                },
              },
              grades: {
                meta: { step: steps.admissionType.enem.grades },
                exit: ['updateHistory'],
                on: {
                  PREV: 'subscriptionNumber',
                  NEXT: {
                    target: '#admissions.evaluationResult',
                    cond: {
                      type: 'hasEventProp',
                      property: 'status',
                      values: evaluationStatusValues,
                    },
                    actions: ['setStatus'],
                  },
                },
              },
            },
          },
          secondGraduation: {
            initial: 'institutionInfo',
            states: {
              institutionInfo: {
                meta: { step: steps.admissionType.secondGraduation.institutionInfo },
                exit: ['updateHistory'],
                on: {
                  PREV: '#admissions.admissionType',
                  NEXT: {
                    target: '#admissions.evaluationResult',
                    cond: {
                      type: 'hasEventProp',
                      property: 'status',
                      values: evaluationStatusValues,
                    },
                    actions: ['setStatus'],
                  },
                },
              },
              documentation: {
                meta: { step: steps.admissionType.secondGraduation.documentation },
                exit: ['updateHistory'],
                on: {
                  PREV: 'institutionInfo',
                  NEXT: {
                    target: '#admissions.evaluationResult',
                    cond: {
                      type: 'hasEventProp',
                      property: 'status',
                      values: evaluationStatusValues,
                    },
                    actions: ['setStatus'],
                  },
                },
              },
            },
          },
          transferGraduation: {
            initial: 'institutionInfo',
            states: {
              institutionInfo: {
                meta: { step: steps.admissionType.transferGraduation.institutionInfo },
                exit: ['updateHistory'],
                on: {
                  PREV: '#admissions.admissionType',
                  NEXT: 'documentation',
                },
              },
              documentation: {
                meta: { step: steps.admissionType.transferGraduation.documentation },
                exit: ['updateHistory'],
                on: {
                  PREV: 'institutionInfo',
                  NEXT: {
                    target: '#admissions.evaluationResult',
                    cond: {
                      type: 'hasEventProp',
                      property: 'status',
                      values: evaluationStatusValues,
                    },
                    actions: ['setStatus'],
                  },
                },
              },
            },
          },
        },
      },
      evaluationResult: {
        always: [
          {
            target: 'success',
            cond: { type: 'evaluateStatus', status: 'success' },
          },
          {
            target: 'failure',
            cond: { type: 'evaluateStatus', status: 'failure' },
          },
          {
            target: 'pendingApproval',
            cond: { type: 'evaluateStatus', status: 'pendingApproval' },
          },
          {
            target: 'retryEssay',
            cond: { type: 'evaluateStatus', status: 'retryEssay' },
          },
        ],
      },
      retryEssay: {
        meta: { step: steps.retryEssay },
        exit: ['updateHistory'],
        on: {
          NEXT: {
            target: 'admissionType.selection',
            actions: ['setRetryCount', 'setRetryHistory'],
          },
        },
      },
      success: {
        meta: { step: steps.success },
        entry: ['updateHistory'],
      },
      failure: {
        meta: { step: steps.failure },
        entry: ['updateHistory'],
      },
      pendingApproval: {
        meta: { step: steps.pendingApproval },
        entry: ['updateHistory'],
      },
    },
    on: {
      ...globalEvents,
    },
  },
  {
    actions,
    guards,
  },
);
